import type { OrderProviderActivityResponse } from '@skupreme/api-contracts/core';
import { CommonUtility } from '@utility/common';
import type {
  CarrierTrackingResponse,
  OrderAddressUpdateRequest,
  OrderAddressUpdateResponse,
  OrderAutocompleteResponse,
  OrderCancelRequest,
  OrderCancelResponse,
  OrderCancellationReasonsResponse,
  OrderDetail,
  OrderDuplicateRequest,
  OrderDuplicateResponse,
  OrderLogsResponse,
  OrderManualCancelRequest,
  OrderManualCancelResponse,
  OrderMatchItemsRequest,
  OrderMemoResponse,
  OrderMemoUpdateRequest,
  OrderMemoUpdateResponse,
  OrderOverviewResponse,
  OrderPaginationResponse,
  OrderRefundRequest,
  OrderRefundResponse,
  OrderStateUpdateRequest,
  OrderShippingRatesResponse,
  OrderStateUpdateResponse,
  OrderStatusCountResponse,
  OrderValidateResponse,
} from '@skupreme/api-contracts/core';
import { APIPath } from '../constants/api';
import type {
  AssignRoutingBody,
  EditPlanBody,
  EditPlanReply,
  LineCandidates,
  OrderPlanView,
  RoutingTrail,
} from '@features/orders/lib/routing-types';

/** EN-4308: the bodies of the V2 order actions; `eventId` is the idempotency key. */
export type CloseExceptionBody = {
  eventId: string;
  status: 'resolved' | 'dismissed';
  note: string;
};
export type ReleaseHoldBody = { eventId: string; reason: string };
/** What a 200 carries; a refusal is a 409 with the same shape. */
export type OrderActionReply = { accepted?: boolean; reason?: string };

/**
 * EN-4188: GET orders/:orderId/labels/options, the seller's own carrier
 * accounts that can buy this order a label. A payor is told by its last four.
 */
export type OwnLabelAccount = {
  id: string;
  name: string;
  kind: 'own' | 'third_party';
  isDefault: boolean;
  payorLast4?: string;
  nodeIds: string[];
  /** The ship-from nodes by name, when the API serves them; a name may be null. */
  nodes?: Array<{ id: string; name: string | null }>;
};
/** EN-2619 M7: one box the API proposes from the order's lines; weight null = unknown. */
export type OwnLabelPackageDefault = {
  weightOz: number | null;
  lengthIn?: number;
  widthIn?: number;
  heightIn?: number;
  dimWeightLb?: number;
  lines: Array<{ orderLineId: string; quantity: number }>;
};
export type OwnLabelPackageDefaults = {
  packages: OwnLabelPackageDefault[];
  /** Lines with no weight / no dimensions on record. */
  missing: { weight: string[]; dims: string[] };
  cartonSuggestions: OwnLabelCartonSuggestion[];
};
export type OwnLabelCartonSuggestion = {
  orderLineId: string;
  unitsPerCarton: number;
  lengthIn: number;
  widthIn: number;
  heightIn: number;
  grossWeightOz: number;
};
export type OwnLabelBoxPreset = {
  name: string;
  lengthIn: number;
  widthIn: number;
  heightIn: number;
};
/** The EN-2619 M7 fields are absent from an older API. */
export type OwnLabelOptions = {
  accounts: OwnLabelAccount[];
  services: string[];
  unavailable?: true;
  packageDefaults?: OwnLabelPackageDefaults;
  boxPresets?: OwnLabelBoxPreset[];
  /** Dimensional weight divisor (in³ per lb). */
  dimDivisor?: number;
};
export type OwnLabelBilling =
  | { kind: 'own' }
  | { kind: 'third_party'; carrierBillingAccountId: string };
export type OwnLabelPackageBody = {
  seq: number;
  weightOz: number;
  lengthIn?: number;
  widthIn?: number;
  heightIn?: number;
  /** Absent only with a single package: it then carries the whole order. */
  lines?: Array<{ orderLineId: string; quantity: number }>;
};
export type BuyOwnLabelBody = {
  /** Null ships from the account's default. */
  shipFromNodeId: string | null;
  service: string;
  signature: boolean;
  billing: OwnLabelBilling;
  packages: OwnLabelPackageBody[];
};
/** A 200 or a 409 to a buy names the label the API made for it. */
export type BuyOwnLabelReply = OrderActionReply & { labelId?: string };
export type VoidOwnLabelBody = {
  reason: 'refunded' | 'superseded' | 'canceled' | 'other';
};
/**
 * EN-2619 M7: PUT orders/:orderId/items/:itemId/dimensions, the Buy label
 * dialog's "Save to item". Whole millimetres and grams; length, width and
 * height all or none, and the dimensions or the weight. `eventId` is the
 * idempotency key (the API makes one when absent).
 */
export type SaveItemDimensionsBody = {
  eventId?: string;
  lengthMm?: number;
  widthMm?: number;
  heightMm?: number;
  weightG?: number;
};
/** A 200 is `written` or `unchanged`; a refusal is a 409 `refused` with its reason. */
export type SaveItemDimensionsReply = {
  outcome: 'written' | 'unchanged' | 'refused';
  reason?: string;
};

/**
 * GET orders/live (EN-4117). `v2` is true when the account reads the orders page from Postgres
 * (switch orders_page_v2); the page then polls `version` every `poll_ms` instead of listening to Firebase.
 */
export type OrdersLiveResponse =
  | { v2: false }
  | { v2: true; version: string; poll_ms: number };
import { activeScopeParams } from '@features/account-view/active';
import { scopedWrite } from '@features/account-view/scoped-write';
import type { OrderRatesQuery } from '@features/orders/lib/order-rates';
import { CRUDService } from './crud';
import { BaseService } from './base';

/**
 * EN-4396: every orders READ carries the active level's params (`scope`,
 * `division`); at the division level there are none and the url is unchanged.
 * Writes do not go through here.
 */
const scoped = (url: string): string => {
  const extra = CommonUtility.objectToParams(activeScopeParams());
  if (!extra) return url;
  const joiner = !url.includes('?') ? '?' : /[?&]$/.test(url) ? '' : '&';
  return `${url}${joiner}${extra}`;
};

class Orders extends CRUDService {
  constructor() {
    super(APIPath.orders);
  }

  get(params = {}, signal?: AbortSignal) {
    const query = CommonUtility.objectToParams(params);
    return BaseService.get(scoped(`${this.url}?${query}`), false, true, signal);
  }

  getById = (id: string): Promise<OrderDetail> =>
    BaseService.get(scoped(`${APIPath.orders}/${encodeURIComponent(id)}`));

  pageData = (
    params,
    signal?: AbortSignal,
  ): Promise<OrderPaginationResponse> => {
    const query = CommonUtility.objectToParams(params);
    const url = `${APIPath.orderPagination}?${query}`;
    return BaseService.get(scoped(url), false, true, signal);
  };

  cancelOrder = (reqData: OrderCancelRequest): Promise<OrderCancelResponse> =>
    BaseService.put(APIPath.cancelOrder, reqData);

  manualCancelOrder = (
    reqData: OrderManualCancelRequest,
  ): Promise<OrderManualCancelResponse> =>
    BaseService.put(`${APIPath.manualCancelOrder}`, reqData);

  /* EN-4117: on V2 the counts follow the list's filters, so they travel as the query. */
  statusCount = (
    params?: Record<string, unknown>,
  ): Promise<OrderStatusCountResponse> =>
    BaseService.get(
      scoped(
        params
          ? `${APIPath.ordersStatusCount}?${CommonUtility.objectToParams(params)}`
          : APIPath.ordersStatusCount,
      ),
    );

  live = (): Promise<OrdersLiveResponse> =>
    BaseService.get(scoped(APIPath.ordersLive));

  /* EN-4308: the V2 order actions. `orderId` is the order's Postgres id (pg_id). */
  private orderAction = (
    orderId: string,
    path: string,
    body:
      | CloseExceptionBody
      | ReleaseHoldBody
      | AssignRoutingBody
      | BuyOwnLabelBody
      | VoidOwnLabelBody,
    url: (to: string) => string = to => to,
  ): Promise<OrderActionReply> =>
    BaseService.post(
      url(`${APIPath.orders}/${encodeURIComponent(orderId)}/${path}`),
      body,
    );

  closeException = (
    orderId: string,
    exceptionId: string,
    body: CloseExceptionBody,
  ) =>
    this.orderAction(
      orderId,
      `exceptions/${encodeURIComponent(exceptionId)}/close`,
      body,
    );

  releaseHold = (orderId: string, body: ReleaseHoldBody) =>
    this.orderAction(orderId, 'release', body, scopedWrite);

  /*
   * EN-4204: the order's routing, read from the planner, and its one write,
   * the planner's operatorEditPlan. `orderId` is the order's Postgres id (pg_id).
   */
  private orderPath = (orderId: string, path: string) =>
    `${APIPath.orders}/${encodeURIComponent(orderId)}/${path}`;

  getPlan = (orderId: string): Promise<OrderPlanView> =>
    BaseService.get(scoped(this.orderPath(orderId, 'plan')));

  getLineCandidates = (
    orderId: string,
    lineId: string,
  ): Promise<LineCandidates> =>
    BaseService.get(
      scoped(
        this.orderPath(
          orderId,
          `lines/${encodeURIComponent(lineId)}/candidates`,
        ),
      ),
    );

  getRoutingTrail = (orderId: string): Promise<RoutingTrail> =>
    BaseService.get(scoped(this.orderPath(orderId, 'routing')));

  /** EN-4134: what we sent each provider about the order and what they answered. */
  getProviderActivity = (
    orderId: string,
  ): Promise<OrderProviderActivityResponse> =>
    BaseService.get(scoped(this.orderPath(orderId, 'provider-activity')));

  editPlan = (orderId: string, body: EditPlanBody): Promise<EditPlanReply> =>
    BaseService.post(this.orderPath(orderId, 'plan/edits'), body);

  /**
   * EN-3966: the operator's own routing decision (AssignRouting, `POST
   * /orders/:orderId/routing`): a suggestion confirmed, or an order the
   * planner has not chosen a plan for assigned. The planner edit only pins a
   * shadow plan unless the planner is automatic; this one routes and sends.
   */
  assignRouting = (orderId: string, body: AssignRoutingBody) =>
    this.orderAction(orderId, 'routing', body, scopedWrite);

  /* EN-4188: a label on the seller's own carrier account. */
  labelOptions = (orderId: string): Promise<OwnLabelOptions> =>
    BaseService.get(scoped(this.orderPath(orderId, 'labels/options')));

  /* EN-4544: the order's shipping rates for one package (EN-4543). Read only. */
  shippingRates = (
    orderId: string,
    query: OrderRatesQuery,
  ): Promise<OrderShippingRatesResponse> =>
    BaseService.get(
      scoped(
        `${this.orderPath(orderId, 'rates')}?${CommonUtility.objectToParams(query)}`,
      ),
    );

  /** The API makes the label id; the label is bought once it has answered. */
  buyOwnLabel = (
    orderId: string,
    body: BuyOwnLabelBody,
  ): Promise<BuyOwnLabelReply> => this.orderAction(orderId, 'labels', body);

  voidOwnLabel = (orderId: string, labelId: string, body: VoidOwnLabelBody) =>
    this.orderAction(
      orderId,
      `labels/${encodeURIComponent(labelId)}/void`,
      body,
    );

  /** Runs after a buy succeeded; a failure never fails or retries the label. */
  saveItemDimensions = (
    orderId: string,
    itemId: string,
    body: SaveItemDimensionsBody,
  ): Promise<SaveItemDimensionsReply> =>
    BaseService.put(
      `${APIPath.orders}/${encodeURIComponent(orderId)}/items/${encodeURIComponent(itemId)}/dimensions`,
      body,
    );

  updateAddress = (
    data: OrderAddressUpdateRequest,
  ): Promise<OrderAddressUpdateResponse> =>
    BaseService.put(APIPath.orderAddress, data);

  getMemo = (id: string): Promise<OrderMemoResponse> =>
    BaseService.get(scoped(`${APIPath.ordersMemo}/${id}`));

  updateMemo = (
    data: OrderMemoUpdateRequest,
  ): Promise<OrderMemoUpdateResponse> =>
    BaseService.put(APIPath.ordersMemo, data);

  duplicate = (data: OrderDuplicateRequest): Promise<OrderDuplicateResponse> =>
    BaseService.post(APIPath.orderDuplicate, data);

  validate = (marketOrderId: string): Promise<OrderValidateResponse> =>
    BaseService.get(`${APIPath.orderValidate}/${marketOrderId}`);

  addressSuggestion = data =>
    BaseService.post(APIPath.orderAddressSuggestion, data);

  carrierTrackingStatus = (
    provider: string,
    trackingId: string,
  ): Promise<CarrierTrackingResponse> =>
    BaseService.get(`${APIPath.carrierTracking}/${provider}/${trackingId}`);

  orderCancellationReasonsList =
    (): Promise<OrderCancellationReasonsResponse> =>
      BaseService.get(`${APIPath.orderCancellationReasonsList}`);

  refundOrder = (data: OrderRefundRequest): Promise<OrderRefundResponse> =>
    BaseService.put(`${APIPath.refundOrder}`, data);

  updateOrderStatus = (
    id: string,
    data: OrderStateUpdateRequest,
  ): Promise<OrderStateUpdateResponse> =>
    BaseService.put(`${APIPath.updateOrderStatus}/${id}`, data);

  getAutocomplete = (
    params,
    signal?: AbortSignal,
  ): Promise<OrderAutocompleteResponse> => {
    const query = CommonUtility.objectToParams(params);
    return BaseService.get(
      `${APIPath.ordersAutocomplete}?${query}`,
      false,
      true,
      signal,
    );
  };

  getLogs = (
    orderId: string,
    signal?: AbortSignal,
  ): Promise<OrderLogsResponse> =>
    BaseService.get(
      scoped(`${APIPath.orderLogs}/${orderId}`),
      false,
      true,
      signal,
    );

  getOrdersOverview = (signal?: AbortSignal): Promise<OrderOverviewResponse> =>
    BaseService.get(APIPath.ordersOverview, false, true, signal);

  getPublicOrder = token => BaseService.get(`${APIPath.orderPublic}/${token}`);

  generateShippingRate = (token, data) =>
    BaseService.post(`${APIPath.multipleShippingLabelRate}/${token}`, data);

  generatePublicShippingLabel = (token, data) =>
    BaseService.post(`${APIPath.orderPublic}/${token}`, data);

  providerShippingRate = data =>
    BaseService.post(APIPath.providerShippingRate, data);

  providerReturnsRate = (data, signal?: AbortSignal) =>
    BaseService.post(
      APIPath.providerReturnRate,
      data,
      false,
      true,
      undefined,
      signal,
    );

  providerFBAShippingRate = data =>
    BaseService.post(APIPath.providerFBAShippingRate, data);

  getUploadLabelUrl = carrier_code =>
    BaseService.get(`${APIPath.shippingLabelUpload}/${carrier_code}`);

  generateOrderPublicUrl = payload =>
    BaseService.post(APIPath.orderPublicLink, payload);

  putMatchOrderItem = (
    id: string,
    data: OrderMatchItemsRequest,
  ): Promise<OrderDetail> =>
    BaseService.put(`${APIPath.ordersMatchItems}/${id}`, data);

  exportOrder = payload => BaseService.post(APIPath.exportOrders, payload);

  downloadFeedTemplate = reqParams => {
    const params = CommonUtility.objectToParams(reqParams);
    return BaseService.get(`${APIPath.orderFeedsTemplate}?${params}`);
  };

  feedList = filter => {
    const params = CommonUtility.objectToParams(filter);
    return BaseService.get(`${APIPath.orderFeeds}/?${params}`);
  };

  getOrderFeedUpload = (feedType, reqParams) => {
    const params = CommonUtility.objectToParams(reqParams);
    return BaseService.get(
      `${APIPath.orderFeedsUploadUrl}/${feedType}/?${params}`,
    );
  };

  exportOrderHistory = filter => {
    const params = CommonUtility.objectToParams(filter);
    return BaseService.get(`${APIPath.exportOrders}/?${params}`);
  };

  updateBlockPartialFulfillment = (regionId, data) =>
    BaseService.put(`${APIPath.fpFulfillmentBlockPartial}/${regionId}`, data);
}

const OrdersService = new Orders();
Object.freeze(OrdersService);
export default OrdersService;
